import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SVG } from "../../../constants/assetLists";
import {
  OrganizationBranch,
  OrganizationNode,
  OrganizationRoot,
  OrganizationSubBranch,
} from "../../../services/organizations";
import { useOrganizationsScreenContext } from "../../../context/organizations/OrganizationsScreenContextProvider";

type Props = {
  isDefaultParentSelected: boolean;
  onDefaultParentSelect: () => void;
  onLocationSelect: (node: OrganizationNode) => void;
};

function OrganizationsSideFilter({
  isDefaultParentSelected,
  onDefaultParentSelect,
  onLocationSelect,
}: Props) {
  const { i18n } = useTranslation();
  const {
    organizations,
    selectedSideFilterOrganization,
    setSelectedSideFilterOrganization,
    isDefaultParentExpanded,
    setIsDefaultParentExpanded,
  } = useOrganizationsScreenContext();

  const sidebarRef = useRef<HTMLDivElement>(null);
  const [filterSidebarVariant, setFilterSidebarVariant] = useState<
    "full" | "compact" | "hidden"
  >("full");
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const hasSeededExpansion = useRef(false);

  const isHebrew = useMemo(() => i18n.language === "heb", [i18n.language]);
  const isFull = useMemo(
    () => filterSidebarVariant === "full",
    [filterSidebarVariant],
  );
  const isCompact = useMemo(
    () => filterSidebarVariant === "compact",
    [filterSidebarVariant],
  );

  // Expand every network once, when the organizations first load
  useEffect(() => {
    if (hasSeededExpansion.current || !organizations.length) return;
    hasSeededExpansion.current = true;
    setExpandedSet(
      (prev) => new Set([...prev, ...organizations.map((node) => node.id)]),
    );
  }, [organizations]);

  // Reveal the selected node by expanding its ancestors (union, never collapses)
  useEffect(() => {
    if (!selectedSideFilterOrganization) return;
    const { isBranch, isSubBranch, clientId, branchId } =
      selectedSideFilterOrganization as OrganizationNode & {
        clientId?: string;
        branchId?: string;
      };
    const ancestorIds = isSubBranch
      ? [clientId, branchId]
      : isBranch
        ? [clientId]
        : [];
    const idsToExpand = ancestorIds.filter((id): id is string => !!id);
    if (!idsToExpand.length) return;

    setExpandedSet((prev) =>
      idsToExpand.every((id) => prev.has(id))
        ? prev
        : new Set([...prev, ...idsToExpand]),
    );
  }, [selectedSideFilterOrganization]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !sidebarRef.current) return;

      const newWidth = isHebrew ? window.innerWidth - e.clientX : e.clientX;

      if (newWidth >= 209) {
        setFilterSidebarVariant("full");
        setIsDragging(false);
        return;
      }

      sidebarRef.current.style.width = `${Math.max(newWidth, 86)}px`;
    };

    const stopDragging = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", stopDragging);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
    };
  }, [isDragging, isHebrew]);

  useEffect(() => {
    if (!sidebarRef.current) return;

    sidebarRef.current.style.width = isFull
      ? "302px"
      : isCompact
        ? "86px"
        : "24px";
  }, [filterSidebarVariant, isCompact, isFull]);

  const handleSidebarWidthChange = () => {
    setFilterSidebarVariant((prev) =>
      prev === "full" ? "compact" : prev === "compact" ? "hidden" : "full",
    );
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const toggleExpand = (id: string) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelect = (node: OrganizationNode) => {
    setSelectedSideFilterOrganization(node);
    onLocationSelect(node);
  };

  const handleDefaultParentClick = () => {
    setIsDefaultParentExpanded((prev) => !prev);
    onDefaultParentSelect();
  };

  const TreeItem: React.FC<{
    node: OrganizationRoot | OrganizationBranch | OrganizationSubBranch;
    depth: number;
  }> = ({ node, depth }) => {
    const children: (OrganizationBranch | OrganizationSubBranch)[] =
      node.isClient
        ? (node as OrganizationRoot).branches || []
        : node.isBranch
          ? (node as OrganizationBranch).subBranches || []
          : [];

    const hasChildren = children.length > 0;
    const expanded = expandedSet.has(node.id);
    const isSelected =
      !isDefaultParentSelected &&
      selectedSideFilterOrganization?.id === node.id;
    const isArchived = !!node.isArchived;
    const isDraft = !isArchived && node.status === "draft";
    const isInactive = !isArchived && node.status === "inactive";
    const paddingInline = isFull ? Math.min(12 + depth * 16, 56) : 0;
    const rounded = isHebrew
      ? "rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px]"
      : "rounded-tr-[30px] rounded-br-[30px] rounded-tl-[6px] rounded-bl-[6px]";
    const caretIcon = expanded ? SVG.dropdownIcon : SVG.dropUpIcon;

    const onClick = () => {
      if (hasChildren) toggleExpand(node.id);
      handleSelect(node);
    };

    return (
      <div>
        <button
          key={node.id}
          className={`w-full flex items-center gap-1 py-2 transition-colors duration-200 hover:bg-hoverBg
            ${isFull ? "justify-between" : "justify-center"} ${rounded}
            ${isSelected ? "bg-softAqua" : ""}
          `}
          style={{
            paddingInlineStart: `${paddingInline}px`,
            paddingInlineEnd: "8px",
          }}
          onClick={onClick}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-2">
              {hasChildren && (
                <div className="w-[15px] h-[10px]">
                  <img
                    src={caretIcon}
                    alt="caret"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <div className="w-[20px] h-[20px]">
                <img
                  src={node.icon}
                  alt="icon"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            {isFull && (
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-black truncate">{node?.name}</span>
                {isArchived && (
                  <span className="flex-shrink-0 px-[8px] py-[2px] rounded-[6px] border-[1px] border-[#E52055] bg-[#FFFFFF] text-[#E52055] text-xs leading-4">
                    נמחק
                  </span>
                )}
                {isDraft && (
                  <span className="flex-shrink-0 px-[8px] py-[2px] rounded-[6px] border-[1px] border-[#707070] bg-[#E5E5E5] text-[#707070] text-xs leading-4">
                    טיוטה
                  </span>
                )}
                {isInactive && (
                  <span className="flex-shrink-0 px-[8px] py-[2px] rounded-[6px] border-[1px] border-[#B9B9B9] bg-[#B9B9B9] text-[#000000] text-xs leading-4">
                    לא פעיל
                  </span>
                )}
              </div>
            )}
          </div>
          {isFull && (
            <span className="text-black flex-shrink-0">
              {hasChildren ? children.length : ""}
            </span>
          )}
        </button>

        {hasChildren && expanded && (
          <div className="mt-1 flex flex-col gap-1">
            {children.map((child) => (
              <TreeItem key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={sidebarRef}
      className={`flex-none relative transition-all duration-150 h-full bg-[#f4f4f4] ${
        i18n.language === "heb"
          ? `border-l-2 ${isDragging ? "border-l-[#007AFF]" : "border-l-black"}`
          : `border-r-2 ${isDragging ? "border-r-[#007AFF]" : "border-r-black"}`
      }`}
    >
      <div
        className={`${
          filterSidebarVariant !== "hidden" && "px-3"
        } py-3 border-b border-b-lightDarkGray`}
      >
        <div
          className={`flex ${
            isFull ? "justify-between" : "justify-center"
          } items-center`}
        >
          {isFull && (
            <span className="text-[20px] font-light leading-6 text-black">
              מקומות
            </span>
          )}

          <button
            className="w-[12px] h-[12px] hover:opacity-40"
            onClick={handleSidebarWidthChange}
          >
            <img
              src={
                filterSidebarVariant === "hidden"
                  ? SVG.lessThanIcon
                  : SVG.greaterThanIcon
              }
              alt="toggle"
              className="w-full h-full object-contain"
            />
          </button>
        </div>
      </div>

      <div className="w-full h-full">
        {(isFull || isCompact) && (
          <div className="w-full h-full flex flex-col gap-2 py-3">
            <div className="flex-1 min-h-0 flex flex-col px-3 gap-2 overflow-y-auto">
              <button
                className={`w-full flex items-center py-2 transition-colors duration-200 hover:bg-hoverBg
                  ${isFull ? "justify-between" : "justify-center"}
                  ${isHebrew ? "rounded-tl-[30px] rounded-bl-[30px] rounded-tr-[6px] rounded-br-[6px]" : "rounded-tr-[30px] rounded-br-[30px] rounded-tl-[6px] rounded-bl-[6px]"}
                  ${isDefaultParentSelected ? "bg-softAqua" : ""}
                `}
                style={{
                  paddingInlineStart: isFull ? "12px" : 0,
                  paddingInlineEnd: "8px",
                }}
                onClick={handleDefaultParentClick}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {organizations.length > 0 && (
                    <div className="w-[15px] h-[10px] flex-shrink-0">
                      <img
                        src={
                          isDefaultParentExpanded
                            ? SVG.dropdownIcon
                            : SVG.dropUpIcon
                        }
                        alt="caret"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <div className="w-[20px] h-[20px] flex-shrink-0">
                    <img
                      src={SVG.networkParentIcon}
                      alt="icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {isFull && (
                    <span className="text-black text-start whitespace-normal break-words leading-5">
                      כל הלקוחות
                    </span>
                  )}
                </div>
                {isFull && (
                  <span className="text-black flex-shrink-0">
                    {organizations.length}
                  </span>
                )}
              </button>

              {isDefaultParentExpanded &&
                organizations.map((root) => (
                  <TreeItem key={root.id} node={root} depth={1} />
                ))}
            </div>
          </div>
        )}
      </div>

      {isCompact && (
        <div
          className="absolute top-0 bottom-0 w-[6px] cursor-col-resize z-1"
          style={{
            [i18n.language === "heb" ? "left" : "right"]: 0,
          }}
          onMouseDown={handleMouseDown}
        />
      )}
    </div>
  );
}

export default OrganizationsSideFilter;
